import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Coins, TrendingUp, Users, Shield, Flame, Gift } from 'lucide-react';
import './Tokenomics.css';

const Tokenomics = () => {
  const canvasRef = useRef(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const utilities = [
    {
      icon: <Coins size={24} />,
      title: 'Transaction Gas Fees',
      description: 'All network operations consume $NOMOX as unified gas currency across all connected L1 ecosystems',
    },
    {
      icon: <Shield size={24} />,
      title: 'Validator Staking',
      description: 'Stake $NOMOX to participate in consensus, earn block rewards, and secure the network',
    },
    {
      icon: <Users size={24} />,
      title: 'Governance & Voting',
      description: 'Propose and vote on chain upgrades, treasury allocation, and policy decisions',
    },
    {
      icon: <TrendingUp size={24} />,
      title: 'Liquidity Provision',
      description: 'Provide liquidity to earn fees and use as collateral for cross-chain bridges',
    },
    {
      icon: <Flame size={24} />,
      title: 'Deflationary Burns',
      description: 'Portion of transaction fees are permanently burned, reducing total supply over time',
    },
    {
      icon: <Gift size={24} />,
      title: 'Ecosystem Rewards',
      description: 'Developer grants, community airdrops, and fee rebates on Nomoex Exchange',
    },
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 100;

    const nodes = [
      { label: 'Users', angle: 0, color: '#00d4ff' },
      { label: 'Treasury', angle: 72, color: '#00d4ff' },
      { label: 'Burn', angle: 144, color: '#ff0000' },
      { label: 'Validators', angle: 216, color: '#00ff00' },
      { label: 'Ecosystem', angle: 288, color: '#ffff00' },
    ];

    let flowProgress = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw circular flow path
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.3)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw nodes
      nodes.forEach((node, index) => {
        const angle = (node.angle * Math.PI) / 180;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;

        // Node circle
        ctx.beginPath();
        ctx.arc(x, y, 12, 0, Math.PI * 2);
        ctx.fillStyle = node.color;
        ctx.shadowBlur = 15;
        ctx.shadowColor = node.color;
        ctx.fill();
        ctx.shadowBlur = 0;

        // Node label
        ctx.fillStyle = '#ffffff';
        ctx.font = '12px Share Tech Mono';
        ctx.textAlign = 'center';
        ctx.fillText(node.label, x, y + 30);
      });

      // Draw flow particle
      const currentAngle = (flowProgress * Math.PI) / 180;
      const particleX = centerX + Math.cos(currentAngle) * radius;
      const particleY = centerY + Math.sin(currentAngle) * radius;

      ctx.beginPath();
      ctx.arc(particleX, particleY, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#00d4ff';
      ctx.shadowBlur = 20;
      ctx.shadowColor = '#00d4ff';
      ctx.fill();
      ctx.shadowBlur = 0;

      flowProgress = (flowProgress + 1) % 360;
      requestAnimationFrame(animate);
    };

    animate();
  }, []);

  return (
    <section id="tokenomics" className="tokenomics-alien" data-testid="tokenomics-section" ref={ref}>
      <div className="section-container">
        <motion.div
          className="section-header-alien"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="section-tag">
            <div className="tag-line"></div>
            <span className="tag-text">TOKEN ECONOMICS</span>
            <div className="tag-line"></div>
          </div>
          <h2 className="section-title-alien">
            <span className="title-normal">$NOMOX</span>
            <span className="title-highlight">TOKENOMICS</span>
          </h2>
          <p className="section-subtitle-alien">
            Native utility token powering the Nomoex multi-chain ecosystem
          </p>
        </motion.div>

        <div className="tokenomics-grid">
          {/* Token Stats */}
          <motion.div
            className="token-stats"
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="stat-box">
              <div className="stat-box-label">Total Supply</div>
              <div className="stat-box-value">2,500,000,000</div>
              <div className="stat-box-unit">$NOMOX</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-label">Token Type</div>
              <div className="stat-box-value">NATIVE</div>
              <div className="stat-box-unit">Migrating from BEP-20</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-label">Initial Circulation</div>
              <div className="stat-box-value">TBA</div>
              <div className="stat-box-unit">At Mainnet Launch</div>
            </div>
          </motion.div>

          {/* Token Flow Visualization */}
          <motion.div
            className="token-flow-viz"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="flow-title">Token Flow Cycle</h3>
            <canvas ref={canvasRef} width="300" height="300" className="flow-canvas" />
            <div className="flow-description">
              Users pay gas → Treasury Pool → Burn + Validator Rewards + Ecosystem Fund → Staking/Grants/Airdrops
            </div>
          </motion.div>
        </div>

        {/* Utility Cards */}
        <motion.div
          className="utilities-grid"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {utilities.map((utility, index) => (
            <motion.div
              key={index}
              className="utility-card-alien"
              whileHover={{ y: -5, scale: 1.02 }}
              data-testid={`utility-card-${index}`}
            >
              <div className="utility-icon-wrapper">
                <div className="utility-icon-alien">{utility.icon}</div>
              </div>
              <h4 className="utility-title-alien">{utility.title}</h4>
              <p className="utility-description-alien">{utility.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Tokenomics;